import { useMemo } from 'react';
import { TargetComparison } from '../../../../../api/targets-client/targets-client.type';
import type { MeasureMetadata } from '../../../../../api/work-periods-client/work-periods-client.type';
import { type MeasureDataResponse } from '../../../../../api/work-periods-client/work-periods-client.type';
import { useProcessAnalysisStore } from '../../../../../store/process-analysis-store/process-analysis-store';
import {
  useSelectedMeasures,
  useSelectedMeasureTrends,
  useTimeAllocation,
  useTimeAllocations,
} from '../../../../../store/process-analysis-store/process-analysis-store.hooks';
import { baseBlack } from '../../../../../styles/design-tokens';
import { getTimeAllocationChartLabels } from '../../../components/line-chart/line-chart.helpers';
import type { ChartDataItem, TargetLineItem } from '../../../components/line-chart/line-chart.type';
import { calculateTrend, getTargetStrokeDashArray, prepareTargetDataForChart } from './line-chart.helpers';

/**
 * Custom hook that transforms the measure data into chart data with targets
 *
 * @param {MeasureDataResponse} data - The measure data to transform
 * @param {MeasureMetadata[]} mergedMeasureMetadata - Measure metadata with targets
 * @returns {ChartDataItem[]} - The chart data with target values
 */
const useChartData = (data: MeasureDataResponse, mergedMeasureMetadata?: MeasureMetadata[]): ChartDataItem[] => {
  const measures = useSelectedMeasures();
  const trends = useSelectedMeasureTrends();

  const timeAllocation = useTimeAllocation();
  const timeAllocations = useTimeAllocations();

  const selectedTargets = useProcessAnalysisStore((state) => state.selectedTargets);

  return useMemo(() => {
    if (!data || !measures.length) {
      return [];
    }

    const allDates = new Set<string>();
    measures.forEach((measure) => {
      const measureValues = data[measure];
      if (measureValues) {
        Object.keys(measureValues).forEach((date) => {
          allDates.add(date);
        });
      }
    });

    // Also include dates from targets if they exist
    if (mergedMeasureMetadata && selectedTargets) {
      selectedTargets.forEach(({ measureId, targetId }) => {
        const measureMetadata = mergedMeasureMetadata.find((m) => m.measure_name === measureId);
        const target = measureMetadata?.targets?.find((t) => t.id === targetId);
        if (target) {
          Object.keys(target.time_allocations).forEach((date) => {
            allDates.add(date);
          });
        }
      });
    }

    const sortedDates = Array.from(allDates).sort();

    const trendCalculations: Record<string, { slope: number; intercept: number }> = {};
    trends.forEach((measure) => {
      const measureData = data[measure];
      if (measureData) {
        const trendResult = calculateTrend(measureData, true); // Assuming zero is valid for now
        if (trendResult) {
          trendCalculations[measure] = trendResult;
        }
      }
    });

    return sortedDates.map((date, index) => {
      const { tickLabel, tooltipLabel } = getTimeAllocationChartLabels({
        rawDate: date,
        timeAllocationType: timeAllocation,
        timeAllocations,
      });

      const chartDataItem: ChartDataItem = {
        date: tickLabel,
        rawDate: date,
        dateRange: tooltipLabel,
      };

      measures.forEach((measure) => {
        const measureValues = data[measure];
        const value = measureValues ? measureValues[date] : null;
        chartDataItem[measure] = value;
      });

      trends.forEach((measure) => {
        const trendCalc = trendCalculations[measure];
        if (trendCalc) {
          const trendValue = trendCalc.slope * index + trendCalc.intercept;
          chartDataItem[`${measure}_trend`] = trendValue;
        }
      });

      // Add target data
      if (mergedMeasureMetadata && selectedTargets) {
        selectedTargets.forEach(({ measureId, targetId }) => {
          const measureMetadata = mergedMeasureMetadata.find((m) => m.measure_name === measureId);
          const target = measureMetadata?.targets?.find((t) => t.id === targetId);
          if (target) {
            const targetData = prepareTargetDataForChart(target, sortedDates);
            if (targetData.isRange) {
              chartDataItem[`${targetId}_lower`] = targetData.lowerData ? targetData.lowerData[index] : null;
              chartDataItem[`${targetId}_upper`] = targetData.upperData ? targetData.upperData[index] : null;
            } else {
              chartDataItem[targetId] = targetData.data ? targetData.data[index] : null;
            }
          }
        });
      }

      return chartDataItem;
    });
  }, [data, measures, trends, timeAllocation, timeAllocations, mergedMeasureMetadata, selectedTargets]);
};

/**
 * Custom hook that creates target line items for the chart
 *
 * @param {MeasureMetadata[]} mergedMeasureMetadata - Measure metadata with targets
 * @param {Record<string, string>} itemColors - Color mapping for measures
 * @returns {TargetLineItem[]} - The target line items for chart visualization
 */
const useTargetLineItems = (
  mergedMeasureMetadata?: MeasureMetadata[],
  itemColors?: Record<string, string>,
): TargetLineItem[] => {
  const selectedTargets = useProcessAnalysisStore((state) => state.selectedTargets);

  return useMemo(() => {
    if (!mergedMeasureMetadata || !selectedTargets || !itemColors) {
      return [];
    }

    return selectedTargets.map(({ measureId, targetId }) => {
      const measureMetadata = mergedMeasureMetadata.find((m) => m.measure_name === measureId);
      const target = measureMetadata?.targets?.find((t) => t.id === targetId);

      if (!target) {
        return {
          id: targetId,
          name: `Target ${targetId}`,
          color: itemColors[measureId] || baseBlack,
          strokeDasharray: '0 0',
          isRange: false,
          target_comparison: TargetComparison.EQ,
        };
      }

      const color = itemColors[measureId] || baseBlack;
      const isRange =
        target.target_comparison === TargetComparison.RANGE_IN ||
        target.target_comparison === TargetComparison.RANGE_EX;

      // Calculate baseValue for correct fill direction (following Strategy module logic)
      const baseValue =
        target.target_comparison === TargetComparison.GT || target.target_comparison === TargetComparison.GTE
          ? ('dataMax' as const)
          : target.target_comparison === TargetComparison.LT || target.target_comparison === TargetComparison.LTE
            ? ('dataMin' as const)
            : undefined;

      return {
        id: targetId,
        name: target.name || `${measureMetadata?.measure_title || measureId} Target`,
        color,
        strokeDasharray: getTargetStrokeDashArray(target.target_comparison),
        isRange,
        baseValue,
        target_comparison: target.target_comparison,
      };
    });
  }, [mergedMeasureMetadata, selectedTargets, itemColors]);
};

export { useChartData, useTargetLineItems };
